import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Divider,
  Box,
  RadioGroup,
  Radio,
  Avatar,
  IconButton,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Styled components for better layout
const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

interface ReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number;
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  // Yeni logo ayarları
  headerType: 'text' | 'logo'; // Başlık tipi: metin mi logo mu
  logoUrl: string; // Logo URL'si (Firebase Storage'dan)
}

interface ReceiptSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (settings: ReceiptDisplaySettings) => void;
  initialSettings: ReceiptDisplaySettings;
  onImageUpload?: (file: File) => Promise<string>; // Logo yükleme fonksiyonu
}

const ReceiptSettingsModal: React.FC<ReceiptSettingsModalProps> = ({
  open,
  onClose,
  onSave,
  initialSettings,
  onImageUpload,
}) => {
  const { t } = useTranslation('salesSystem');
  const [formValues, setFormValues] = React.useState<ReceiptDisplaySettings>(initialSettings);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setFormValues({
        ...initialSettings,
        showCashierName: typeof initialSettings.showCashierName === 'boolean' ? initialSettings.showCashierName : false,
        showReceiptNumber: typeof initialSettings.showReceiptNumber === 'boolean' ? initialSettings.showReceiptNumber : false,
        headerType: initialSettings.headerType || 'text', // Varsayılan olarak metin
        logoUrl: initialSettings.logoUrl || '',
      });
      setLogoError(null);
    }
  }, [open, initialSettings]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const { checked } = event.target as HTMLInputElement;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormValues(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } 
    else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleNumberChange = (name: keyof ReceiptDisplaySettings, value: string | number) => {
    setFormValues(prev => ({ ...prev, [name]: typeof value === 'string' ? parseFloat(value) || 0 : value }));
  };

  // Logo yükleme fonksiyonu
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImageUpload) return;

    // Dosya boyutu kontrolü (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('receiptSettingsModal.errors.logoTooLarge'));
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      setLogoError(t('receiptSettingsModal.errors.imageOnly'));
      return;
    }

    try {
      setLogoUploading(true);
      setLogoError(null);
      
      const logoUrl = await onImageUpload(file);
      setFormValues(prev => ({ ...prev, logoUrl }));
      
      console.log('✅ Logo başarıyla yüklendi:', logoUrl);
    } catch (error: any) {
      console.error('❌ Logo yükleme hatası:', error);
      setLogoError(t('receiptSettingsModal.errors.logoUploadFailed', { message: error.message }));
    } finally {
      setLogoUploading(false);
    }
  };

  // Logo silme fonksiyonu
  const handleLogoDelete = () => {
    setFormValues(prev => ({ ...prev, logoUrl: '' }));
  };

  // Başlık tipi değişikliği
  const handleHeaderTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHeaderType = event.target.value as 'text' | 'logo';
    setFormValues(prev => ({ ...prev, headerType: newHeaderType }));
    
    // Eğer logo seçiliyorsa showReceiptHeader'ı true yap
    if (newHeaderType === 'logo') {
      setFormValues(prev => ({ ...prev, showReceiptHeader: true }));
    }
  };

  const handleSave = () => {
    // Basic validation
    if (!formValues.companyName) {
      console.error("Şirket adı boş olamaz");
      return;
    }
    onSave(formValues);
    onClose(); // Close modal on save
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('receiptSettingsModal.title')}</DialogTitle>
      <DialogContent dividers>
        <StyledForm>
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.headerSettings.title')}</Typography>
          
          {/* Başlık Tipi Seçimi */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{t('receiptSettingsModal.headerSettings.selectType')}</Typography>
            <RadioGroup
              row
              value={formValues.headerType}
              onChange={handleHeaderTypeChange}
            >
              <FormControlLabel 
                value="text" 
                control={<Radio />} 
                label={t('receiptSettingsModal.headerSettings.textHeader')} 
              />
              <FormControlLabel 
                value="logo" 
                control={<Radio />} 
                label={t('receiptSettingsModal.headerSettings.logoHeader')} 
              />
            </RadioGroup>
          </Box>

          {/* Metin Başlık Bölümü */}
          {formValues.headerType === 'text' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('receiptSettingsModal.companyInfo.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="companyName"
                    label={t('receiptSettingsModal.companyInfo.companyName')}
                    value={formValues.companyName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="taxNumber"
                    label={t('receiptSettingsModal.companyInfo.taxNumber')}
                    value={formValues.taxNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
              <TextField
                name="companyAddress"
                label={t('receiptSettingsModal.companyInfo.address')}
                value={formValues.companyAddress}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {/* Logo Başlık Bölümü */}
          {formValues.headerType === 'logo' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('receiptSettingsModal.logoSection.title')}
              </Typography>
              
              {logoError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {logoError}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {formValues.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={formValues.logoUrl}
                      alt={t('receiptSettingsModal.logoSection.logoAlt')}
                      sx={{ width: 80, height: 80 }}
                      variant="rounded"
                    />
                    <Box>
                      <Typography variant="body2" color="success.main" gutterBottom>
                        {t('receiptSettingsModal.logoSection.logoUploaded')}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleLogoDelete}
                      >
                        {t('receiptSettingsModal.logoSection.deleteLogo')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload-button"
                      type="file"
                      onChange={handleLogoUpload}
                      disabled={logoUploading || !onImageUpload}
                    />
                    <label htmlFor="logo-upload-button">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={logoUploading || !onImageUpload}
                      >
                        {logoUploading ? t('receiptSettingsModal.common.uploading') : t('receiptSettingsModal.logoSection.uploadLogo')}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t('receiptSettingsModal.logoSection.logoHint')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="showReceiptHeader"
                checked={formValues.showReceiptHeader}
                onChange={handleChange}
                disabled={formValues.headerType === 'logo'} // Logo seçiliyse otomatik true
              />
            }
            label={t('receiptSettingsModal.headerSettings.showReceiptHeader')}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.printSettings.title')}</Typography>
          <FormControlLabel
            control={<Checkbox name="showVat" checked={formValues.showVat} onChange={handleChange} />}
            label={t('receiptSettingsModal.printSettings.showVat')}
          />
          <TextField
            name="printCopyCount"
            label={t('receiptSettingsModal.printSettings.printCopyCount')}
            type="number"
            value={formValues.printCopyCount}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 1 && value <= 9) {
                handleNumberChange('printCopyCount', value);
              }
            }}
            fullWidth
            variant="outlined"
            size="small"
            helperText={t('receiptSettingsModal.printSettings.printCopyCountHelp')}
            InputProps={{ 
              inputProps: { min: 1, max: 9 }
            }}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.footerSettings.title')}</Typography>
          <TextField
            name="standardFooterNote"
            label={t('receiptSettingsModal.footerSettings.standardNote')}
            value={formValues.standardFooterNote}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            size="small"
          />
          <TextField
            name="thankYouMessage"
            label={t('receiptSettingsModal.footerSettings.thankYouMessage')}
            value={formValues.thankYouMessage}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="small"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.formatSettings.title')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="receiptWidth"
                label={t('receiptSettingsModal.formatSettings.receiptWidth')}
                type="number"
                value={formValues.receiptWidth}
                onChange={(e) => handleNumberChange('receiptWidth', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="characterLength"
                label={t('receiptSettingsModal.formatSettings.characterLength')}
                type="number"
                value={formValues.characterLength}
                onChange={(e) => handleNumberChange('characterLength', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
          
          <Typography variant="subtitle1" gutterBottom sx={{mt: 2}}>{t('receiptSettingsModal.formatSettings.pageMargins')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginTop"
                label={t('receiptSettingsModal.formatSettings.marginTop')}
                type="number"
                value={formValues.marginTop}
                onChange={(e) => handleNumberChange('marginTop', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginBottom"
                label={t('receiptSettingsModal.formatSettings.marginBottom')}
                type="number"
                value={formValues.marginBottom}
                onChange={(e) => handleNumberChange('marginBottom', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginLeft"
                label={t('receiptSettingsModal.formatSettings.marginLeft')}
                type="number"
                value={formValues.marginLeft}
                onChange={(e) => handleNumberChange('marginLeft', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginRight"
                label={t('receiptSettingsModal.formatSettings.marginRight')}
                type="number"
                value={formValues.marginRight}
                onChange={(e) => handleNumberChange('marginRight', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </StyledForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('receiptSettingsModal.common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">{t('receiptSettingsModal.common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptSettingsModal;
